import { setItemVisual } from './icons';
import { Inventory, ITEMS, type ItemId } from '../game/inventory';
import { RECIPES } from './crafting';
import { RESEARCH, DEFAULT_RESEARCH_COST, isResearchNode, researchState } from '../game/research';
import { isUnlocked } from '../game/unlocks';
import { SK, injectSkinCss } from './skin';
import { buildShell, type Shell } from './shell';

// The RESEARCH TREE screen (opened at a placed research_table). Renders the real tech GRAPH from game/research.ts
// -- nodes at their authored x/y, edges drawn from each node's prerequisites, tier-coloured, and each node shows
// its live state: RESEARCHED (green), AVAILABLE (prereqs met -> click to research for salvage), or LOCKED (a
// prerequisite is still missing). Researching consumes RESEARCH.costs[id] salvage (+ 1 example item for the
// `locked` weapons, the "first-gun moment") and permanently unlocks the recipe (game/unlocks.ts), which is what
// gates crafting once RESEARCH_GATE_ALL is on. Affordability + state live-update as items move (inv.subscribe).

const CARD_W = 188, CARD_H = 82;
const TIER_COLOR: Record<number, string> = { 1: SK.good, 2: SK.info, 3: SK.warn }; // tier chips are the ONLY place blue/gold live (style guide)
const TIER_SOFT: Record<number, string> = { 1: SK.goodSoft, 2: SK.infoSoft, 3: SK.warnSoft };
const SCROLL_PAD_RIGHT = 8; // spec section 1 (body layout A): clear the scrollbar so the graph doesn't sit under it
const SALVAGE: ItemId = 'salvage' as ItemId; // the balance shown in the header badge and spent per node

export class ResearchMenu {
  private readonly shell: Shell;             // THE shared 1240 window (oxide-shell-spec.md section 0 + 1: body layout A)
  private readonly scroll: HTMLDivElement;   // scrollable viewport inside the single padded body
  private readonly canvas: HTMLDivElement;   // absolutely-positioned graph surface (nodes + edge svg)
  private open_ = false;

  onClose: (() => void) | null = null;
  onResearch: ((id: ItemId) => void) | null = null; // main.ts: re-checks cost, consumes salvage(+example), unlock()

  constructor(private readonly inv: Inventory) {
    injectSkinCss();
    // Body layout A (single pane, padded body). Title + sub live in the header; the interaction hint and the
    // Q close chip live in the footer. The salvage balance is a header badge (setBadge), never repeated per node.
    this.shell = buildShell({
      title: 'Research Tree',
      sub: 'Spend salvage to unlock recipes. A node opens once its prerequisites are researched.',
      closeKey: 'Q',
      bodyPad: true,
      hintsHtml: 'Click an open node to research it &middot; scroll to pan the tree',
    });
    const outsideTap = (e: MouseEvent): boolean => e.target === this.shell.scrim;
    this.shell.scrim.addEventListener('mouseup', (e) => { if (e.button === 0 && outsideTap(e)) this.close(); });
    this.shell.scrim.addEventListener('contextmenu', (e) => { e.preventDefault(); if (outsideTap(e)) this.close(); });

    // The scroll container fills the padded body; padding-right clears the scrollbar (spec section 1). Keeps
    // its own inset well look so the graph reads as a canvas.
    this.scroll = document.createElement('div');
    this.scroll.style.cssText = css('height:100%', 'min-height:0', 'overflow:auto', 'position:relative',
      `padding-right:${SCROLL_PAD_RIGHT}px`,
      `border:1px solid ${SK.borderSoft}`, `border-radius:${SK.rMd}`, 'background:rgba(0,0,0,0.22)');
    this.canvas = document.createElement('div');
    this.canvas.style.cssText = css('position:relative');
    this.scroll.appendChild(this.canvas);
    this.shell.body.appendChild(this.scroll);

    document.body.appendChild(this.shell.scrim);

    // affordability + newly-available nodes live-update; the header salvage badge tracks the same trigger.
    this.inv.subscribe(() => { if (this.open_) { this.render(); this.syncBadge(); } });
  }

  // Header salvage balance (.ox-badge--count), left of the close hint. Not repeated per node (spec section 4).
  private syncBadge(): void {
    this.shell.setBadge(`<span class="ox-badge ox-badge--count">${this.inv.count(SALVAGE)} Salvage</span>`);
  }

  get isOpen(): boolean { return this.open_; }
  toggle(): void { this.open_ ? this.close() : this.show(); }

  show(): void {
    if (this.open_) return;
    this.open_ = true;
    this.shell.scrim.style.display = 'flex';
    this.render();
    this.syncBadge();
  }

  close(): void {
    if (!this.open_) return;
    this.open_ = false;
    this.shell.scrim.style.display = 'none';
    this.onClose?.();
  }

  private render(): void {
    this.canvas.replaceChildren();
    const ids = Object.keys(RESEARCH.nodes);
    // Graph bounds (+ card size + margin) so the scroll surface fits every node.
    let maxX = 0, maxY = 0;
    for (const id of ids) { maxX = Math.max(maxX, RESEARCH.nodes[id].x); maxY = Math.max(maxY, RESEARCH.nodes[id].y); }
    const W = maxX + CARD_W + 40, H = maxY + CARD_H + 40;
    this.canvas.style.width = W + 'px';
    this.canvas.style.height = H + 'px';

    // Edges first (behind the cards): one SVG spanning the whole surface, a line per prerequisite.
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', String(W)); svg.setAttribute('height', String(H));
    svg.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none';
    const cx = (id: string): number => RESEARCH.nodes[id].x + CARD_W / 2;
    const cy = (id: string): number => RESEARCH.nodes[id].y + CARD_H / 2;
    for (const id of ids) {
      for (const req of RESEARCH.nodes[id].reqs) {
        if (!isResearchNode(req)) continue; // a default prereq has no node to draw from
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(cx(req))); line.setAttribute('y1', String(cy(req)));
        line.setAttribute('x2', String(cx(id))); line.setAttribute('y2', String(cy(id)));
        // A satisfied prerequisite (the parent is researched) brightens GOOD; an unmet one stays faint.
        const lit = isUnlocked(req as ItemId);
        line.setAttribute('stroke', lit ? 'rgba(111,179,95,0.45)' : 'rgba(255,255,255,0.08)');
        line.setAttribute('stroke-width', lit ? '2' : '1.5');
        svg.appendChild(line);
      }
    }
    this.canvas.appendChild(svg);

    for (const id of ids) this.canvas.appendChild(this.nodeCard(id as ItemId));
  }

  private nodeCard(id: ItemId): HTMLElement {
    const n = RESEARCH.nodes[id];
    const state = researchState(id, (x) => isUnlocked(x as ItemId));
    const salvageCost = RESEARCH.costs[id] ?? DEFAULT_RESEARCH_COST;
    const recipe = RECIPES.find((r) => r.id === id);
    const needsExample = !!recipe?.locked; // guns/explosives/reinforced: hold one to research (first-gun moment)
    const haveSalvage = this.inv.count('salvage' as ItemId) >= salvageCost;
    const haveExample = !needsExample || this.inv.count(id) >= 1;
    const canResearch = state === 'available' && haveSalvage && haveExample;

    const tierCol = TIER_COLOR[n.tier] ?? SK.dim;
    const card = document.createElement('div');
    // The spec's THREE node states: done = good border, available = normal card, locked = faint.
    const border = state === 'researched' ? 'rgba(111,179,95,0.55)' : SK.border;
    card.style.cssText = css('position:absolute', `left:${n.x}px`, `top:${n.y}px`, `width:${CARD_W}px`, `height:${CARD_H}px`, 'box-sizing:border-box',
      'display:flex', 'flex-direction:column', 'gap:3px', 'padding:8px 12px', `border-radius:${SK.rMd}`,
      `background:${SK.surface1}`, `border:1px solid ${border}`, `opacity:${state === 'blocked' ? '0.62' : '1'}`,
      canResearch ? 'cursor:pointer' : 'cursor:default', `transition:border-color ${SK.tFast} ${SK.ease}`);

    const head = document.createElement('div');
    head.style.cssText = css('display:flex', 'align-items:center', 'gap:6px');
    const sw = document.createElement('span');
    sw.style.cssText = css('width:12px', 'height:12px', 'border-radius:3px', `background:${cssHex(ITEMS[id].color)}`, 'flex:none');
    setItemVisual(sw, id, cssHex(ITEMS[id].color));
    const name = document.createElement('div');
    name.textContent = ITEMS[id].name;
    name.style.cssText = css('flex:1', 'min-width:0', `font-size:${SK.fsSmall}`, `color:${state === 'blocked' ? SK.dim : SK.textHi}`, 'font-weight:700', 'overflow:hidden', 'text-overflow:ellipsis', 'white-space:nowrap');
    // Tier BADGE (pill, soft fill) -- the only place blue/gold appear.
    const tier = document.createElement('span');
    tier.textContent = 'T' + n.tier;
    tier.style.cssText = css('font-size:10px', 'font-weight:700', 'letter-spacing:0.12em', 'padding:2px 7px', `border-radius:${SK.rPill}`, `background:${TIER_SOFT[n.tier] ?? SK.surface2}`, `color:${tierCol}`, 'flex:none');
    head.append(sw, name, tier);

    const foot = document.createElement('div');
    foot.style.cssText = css(`font-size:${SK.fsMicro}`, 'margin-top:auto');
    if (state === 'researched') {
      foot.innerHTML = `<span style="color:${SK.good}">✓ Researched</span>`;
    } else if (state === 'blocked') {
      const missing = n.reqs.filter((r) => isResearchNode(r) && !isUnlocked(r as ItemId)).map((r) => ITEMS[r as ItemId]?.name ?? r);
      foot.innerHTML = `<span style="color:${SK.faint}">needs ${missing.length ? missing.join(', ') : 'a prerequisite'}</span>`;
    } else {
      // Costs read dim when affordable, BAD only when short (the global cost rule).
      const salvCol = haveSalvage ? SK.dim : SK.bad;
      const exStr = needsExample ? `, <span style="color:${haveExample ? SK.dim : SK.bad}">1 ${ITEMS[id].name}</span>` : '';
      foot.innerHTML = `<span style="color:${salvCol}">${salvageCost} Salvage</span>${exStr}`
        + (canResearch ? ` <span style="color:${SK.good}">· click to research</span>` : '');
    }
    card.append(head, foot);

    if (canResearch) {
      card.addEventListener('mouseenter', () => { card.style.borderColor = SK.accent; card.style.background = SK.accentSoft; });
      card.addEventListener('mouseleave', () => { card.style.borderColor = border; card.style.background = SK.surface1; });
      card.addEventListener('click', () => this.onResearch?.(id));
    }
    return card;
  }
}

function css(...lines: string[]): string { return lines.join(';'); }
function cssHex(hex: number): string { return '#' + hex.toString(16).padStart(6, '0'); }
